import { useCallback, useEffect, useState } from 'react'
import {
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  MessagesSquare,
  NotebookPen,
  Send,
  UserCheck,
} from 'lucide-react'
import { NOT_ENTITLED, get, write } from '../api'
import { useRoleLabels } from '../roleLabels'
import { dateTime, words, workingDays } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Panel,
  Refusal,
  Spinner,
} from '../ui'
import { useResource } from '../useResource'

// ======================================================================
// MEETINGS AND INTERVIEW NOTES
// ======================================================================
//
// THE HANDLING ROLES' VIEW ONLY. list_meetings is gated on
// view_all_notes, which the subject, the reporter, a witness and a
// companion do not hold, because a list of meetings is a list of who has
// been interviewed — on a small team that names the witnesses as surely
// as the party list does. An interviewee's whole surface is /my-notes.
//
// NOTE VERSIONS ARE INSERT-ONLY. Nothing updates one, so every version
// ever written is still here. There is no edit control in this file and
// there must not be: if notes could be changed after somebody confirmed
// them, "the employee confirmed these notes" would be a claim about a
// document that no longer exists.

export default function Meetings({ caseId, items, capabilities, parties, onReplace }) {
  const [dialog, setDialog] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const canTakeNotes = capabilities.includes('take_notes')

  return (
    <Panel
      eyebrow="Interviews and hearings"
      title="Meetings"
      right={
        canTakeNotes && (
          <Button
            size="sm"
            icon={CalendarPlus}
            onClick={() => setDialog({ kind: 'schedule' })}
          >
            Schedule
          </Button>
        )
      }
    >
      {items.length === 0 ? (
        <Empty icon={MessagesSquare} title="No meetings have been arranged">
          {canTakeNotes
            ? 'Scheduling one records the offer of the right to be accompanied, whether or not anybody comes.'
            : 'Nothing has been arranged on this case.'}
        </Empty>
      ) : (
        <ul className="space-y-2.5">
          {items.map((m) => (
            <li key={m.meeting_id} className="panel-inset overflow-hidden">
              <button
                type="button"
                onClick={() =>
                  setExpanded(expanded === m.meeting_id ? null : m.meeting_id)
                }
                className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition hover:bg-surface-3"
              >
                {expanded === m.meeting_id ? (
                  <ChevronDown size={15} strokeWidth={1.9} className="mt-1 text-faint" />
                ) : (
                  <ChevronRight size={15} strokeWidth={1.9} className="mt-1 text-faint" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-head text-[13px] font-semibold">
                      {words(m.kind)}
                    </span>
                    {m.interviewee_label && (
                      <Chip tone="blue">with {m.interviewee_label}</Chip>
                    )}
                    {m.latest_note_version != null ? (
                      <Chip tone="accent">
                        {m.latest_note_version} note{' '}
                        {m.latest_note_version === 1 ? 'version' : 'versions'}
                      </Chip>
                    ) : (
                      <Chip tone="amber">no notes yet</Chip>
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-faint">
                    {m.scheduled_at && <span>Arranged for {dateTime(m.scheduled_at)}</span>}
                    {m.started_at && <span>Ran from {dateTime(m.started_at)}</span>}
                    {m.ended_at && <span>to {dateTime(m.ended_at)}</span>}
                  </div>

                  {/* THE OFFER AND THE ATTENDANCE ARE SEPARATE FACTS.
                      A timestamp with no name means the right was
                      offered and declined, which is a complete answer.
                      Both blank means nobody can show it was offered. */}
                  <div className="mt-2 text-[11.5px]">
                    {m.companion_offered_at ? (
                      m.companion_name ? (
                        <span className="text-accent">
                          Right to be accompanied offered — {m.companion_name} attended
                        </span>
                      ) : (
                        <span className="text-accent">
                          Right to be accompanied offered, and declined
                        </span>
                      )
                    ) : (
                      <span className="text-amber">
                        No record that the right to be accompanied was offered
                      </span>
                    )}
                  </div>

                  {m.adjustments_note && (
                    <p className="mt-1.5 text-[12px] leading-relaxed text-muted">
                      <span className="text-faint">Adjustments: </span>
                      {m.adjustments_note}
                    </p>
                  )}
                </div>
              </button>

              {expanded === m.meeting_id && (
                <MeetingDetail
                  caseId={caseId}
                  meeting={m}
                  canTakeNotes={canTakeNotes}
                  onMeetingsReplace={onReplace}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {dialog?.kind === 'schedule' && (
        <ScheduleDialog
          caseId={caseId}
          parties={parties}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
    </Panel>
  )
}

function MeetingDetail({ caseId, meeting, canTakeNotes, onMeetingsReplace }) {
  const [versions, setVersions] = useState(null)
  const [confirmations, setConfirmations] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dialog, setDialog] = useState(null)

  // TWO SEPARATE ENDPOINTS, EACH WITH ITS OWN 404. Either can come back
  // NOT_ENTITLED independently, and either one doing so renders nothing
  // for that half rather than an error for the whole meeting.
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [v, c] = await Promise.all([
        get(`/${caseId}/meetings/${meeting.meeting_id}/notes`),
        get(`/${caseId}/meetings/${meeting.meeting_id}/confirmations`),
      ])
      setVersions(v === NOT_ENTITLED ? null : v)
      setConfirmations(c === NOT_ENTITLED ? null : c)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [caseId, meeting.meeting_id])

  useEffect(() => {
    load()
  }, [load])

  const byVersion = new Map((confirmations || []).map((c) => [c.note_version, c]))
  const latest = versions?.length ? Math.max(...versions.map((v) => v.version)) : null

  return (
    <div className="border-t border-line bg-surface/60 px-4 py-4">
      <Refusal error={error} onDismiss={() => setError(null)} />

      {loading ? (
        <Spinner label="Loading the notes" />
      ) : (
        <>
          {versions === null ? null : versions.length === 0 ? (
            <Empty icon={NotebookPen} title="No notes have been written yet">
              {canTakeNotes
                ? 'The first version can be written now. Every later version is added alongside it — nothing is ever overwritten.'
                : null}
            </Empty>
          ) : (
            <ol className="space-y-2.5">
              {[...versions]
                .sort((a, b) => b.version - a.version)
                .map((v) => {
                  const conf = byVersion.get(v.version)
                  return (
                    <li key={v.note_id} className="rounded-md border border-line bg-surface-2 px-3.5 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Chip tone={v.version === latest ? 'accent' : undefined}>
                          version {v.version}
                          {v.version === latest ? ' — latest' : ''}
                        </Chip>
                        {conf ? (
                          conf.responded_at ? (
                            <Chip tone={conf.response === 'confirmed' ? 'accent' : 'amber'}>
                              {conf.response === 'confirmed'
                                ? 'confirmed'
                                : 'commented on'}{' '}
                              {dateTime(conf.responded_at)}
                            </Chip>
                          ) : (
                            <Chip tone="amber">
                              awaiting a response —{' '}
                              {workingDays(conf.working_days_outstanding)} outstanding
                            </Chip>
                          )
                        ) : (
                          <Chip>not sent for confirmation</Chip>
                        )}
                      </div>

                      <div className="mt-2 text-[11.5px] text-faint">
                        Written {dateTime(v.created_at)}
                        {conf && <> · sent {dateTime(conf.sent_at)}</>}
                        {conf?.user_label && <> · to {conf.user_label}</>}
                      </div>

                      <p className="measure mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap text-[12.5px] leading-relaxed">
                        {v.body}
                      </p>

                      {/* THE RESPONSE SITS BESIDE THE NOTE, NEVER INSIDE
                          IT. If the interviewee says "I never said
                          that", the disagreement IS the record. */}
                      {conf?.comment && (
                        <div className="mt-3 rounded-md border-l-2 border-amber-line bg-amber-dim px-3 py-2">
                          <div className="eyebrow mb-1">
                            What {conf.user_label || 'the interviewee'} said in reply
                          </div>
                          <p className="measure whitespace-pre-wrap text-[12.5px] leading-relaxed text-ink">
                            {conf.comment}
                          </p>
                        </div>
                      )}
                    </li>
                  )
                })}
            </ol>
          )}

          {canTakeNotes && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                icon={NotebookPen}
                onClick={() => setDialog({ kind: 'note' })}
              >
                Write a new version
              </Button>
              {latest != null && (
                <Button
                  size="sm"
                  icon={Send}
                  onClick={() => setDialog({ kind: 'send', version: latest })}
                >
                  Send version {latest} for confirmation
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                icon={UserCheck}
                onClick={() => setDialog({ kind: 'update' })}
              >
                Update the meeting
              </Button>
            </div>
          )}
        </>
      )}

      {dialog?.kind === 'note' && (
        <NoteDialog
          caseId={caseId}
          meeting={meeting}
          onClose={() => setDialog(null)}
          onDone={(next) => {
            setVersions(next)
            load()
          }}
        />
      )}
      {dialog?.kind === 'send' && (
        <SendDialog
          caseId={caseId}
          meeting={meeting}
          version={dialog.version}
          onClose={() => setDialog(null)}
          onDone={(next) => setConfirmations(next)}
        />
      )}
      {dialog?.kind === 'update' && (
        <UpdateMeetingDialog
          caseId={caseId}
          meeting={meeting}
          onClose={() => setDialog(null)}
          onReplace={onMeetingsReplace}
        />
      )}
    </div>
  )
}

function ScheduleDialog({ caseId, parties, onClose, onReplace }) {
  // The tenant's own word for each role (task 27). Applied at render;
  // the key is what the API sends and what a grant is written with.
  const { label: roleLabel } = useRoleLabels()

  const [kind, setKind] = useState('')
  const [interviewee, setInterviewee] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [companionOffered, setCompanionOffered] = useState(false)
  const [companionName, setCompanionName] = useState('')
  const [adjustments, setAdjustments] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // One configuration read when the dialog opens. It names no case.
  const { data: vocab } = useResource('/vocabularies')
  const kinds = vocab?.meeting_kind ?? []

  const live = (parties || []).filter((p) => !p.revoked_at)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      onReplace(
        await write(`/${caseId}/meetings`, {
          body: {
            kind,
            interviewee_id: interviewee || null,
            scheduled_at: scheduledAt || null,
            companion_offered: companionOffered,
            companion_name: companionName || null,
            adjustments_note: adjustments || null,
          },
        }),
      )
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Schedule a meeting"
      subtitle="Recording that the right to be accompanied was offered is a procedural obligation discharged whether or not anybody comes. “We always offer” is not evidence."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="space-y-4">
        {/* A PICKER SINCE TASK 18, from ck_er_meeting_kind via GET
            /vocabularies — the same change, for the same reason, as the
            evidence kind and the case role. Wired here as well because
            leaving one of the three closed vocabularies as a free-text
            box would mean this screen teaches its values through a
            refusal while the two beside it do not. Free text remains
            the fallback if the fetch failed. */}
        <Field
          label="Kind"
          required
          hint={
            kinds.length
              ? 'What kind of meeting this is. It appears on the invitation and in the bundle.'
              : 'If this is not one of the accepted kinds, the refusal will list them.'
          }
        >
          {kinds.length ? (
            <select
              className="field"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              autoFocus
            >
              <option value="">Choose a kind…</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {words(k)}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="field"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              autoFocus
            />
          )}
        </Field>

        <Field label="Who is being interviewed" required>
          {live.length ? (
            <select
              className="field"
              value={interviewee}
              onChange={(e) => setInterviewee(e.target.value)}
            >
              <option value="">Choose a party…</option>
              {live.map((p) => (
                <option key={p.party_id} value={p.user_id}>
                  {p.user_label || p.user_id} — {roleLabel(p.case_role)}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="field"
              placeholder="User id"
              value={interviewee}
              onChange={(e) => setInterviewee(e.target.value)}
            />
          )}
        </Field>

        <Field label="When">
          <input
            type="datetime-local"
            className="field"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </Field>

        <div className="rounded-md border border-line bg-surface-2 px-4 py-3">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 accent-[color:var(--accent)]"
              checked={companionOffered}
              onChange={(e) => setCompanionOffered(e.target.checked)}
            />
            <span>
              <span className="block font-head text-[12.5px] font-semibold">
                The right to be accompanied was offered
              </span>
              <span className="mt-0.5 block text-[11.5px] leading-relaxed text-faint">
                Tick this if the offer was actually made. It cannot be un-recorded
                afterwards.
              </span>
            </span>
          </label>

          {companionOffered && (
            <div className="mt-3">
              <Field
                label="Who is coming"
                hint="Leave blank if the offer was declined. A recorded offer with no name is a complete answer."
              >
                <input
                  className="field"
                  value={companionName}
                  onChange={(e) => setCompanionName(e.target.value)}
                />
              </Field>
            </div>
          )}
        </div>

        <Field label="Adjustments" hint="Optional.">
          <textarea
            className="field"
            rows={2}
            value={adjustments}
            onChange={(e) => setAdjustments(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" busy={busy} disabled={!kind.trim()} onClick={submit}>
          Schedule
        </Button>
      </div>
    </Modal>
  )
}

function UpdateMeetingDialog({ caseId, meeting, onClose, onReplace }) {
  const [started, setStarted] = useState('')
  const [ended, setEnded] = useState('')
  const [companionName, setCompanionName] = useState(meeting.companion_name || '')
  const [adjustments, setAdjustments] = useState(meeting.adjustments_note || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // ONLY THE FIELDS ACTUALLY CHANGED ARE SENT. The handler uses
  // exclude_unset, so sending just started_at does not blank the
  // companion name — and sending an unchanged value would still write an
  // audit row saying it was changed.
  const submit = async () => {
    setBusy(true)
    setError(null)
    const body = {}
    if (started) body.started_at = started
    if (ended) body.ended_at = ended
    if (companionName !== (meeting.companion_name || ''))
      body.companion_name = companionName
    if (adjustments !== (meeting.adjustments_note || ''))
      body.adjustments_note = adjustments
    try {
      onReplace(
        await write(`/${caseId}/meetings/${meeting.meeting_id}`, {
          method: 'PATCH',
          body,
        }),
      )
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Update the meeting"
      subtitle="When a hearing was arranged for and when it actually ran are different facts, and both are kept."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <div className="space-y-4">
        <Field label="Actually started">
          <input
            type="datetime-local"
            className="field"
            value={started}
            onChange={(e) => setStarted(e.target.value)}
          />
        </Field>
        <Field label="Actually ended">
          <input
            type="datetime-local"
            className="field"
            value={ended}
            onChange={(e) => setEnded(e.target.value)}
          />
        </Field>
        <Field label="Companion who attended">
          <input
            className="field"
            value={companionName}
            onChange={(e) => setCompanionName(e.target.value)}
          />
        </Field>
        <Field label="Adjustments">
          <textarea
            className="field"
            rows={2}
            value={adjustments}
            onChange={(e) => setAdjustments(e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" busy={busy} onClick={submit}>
          Save changes
        </Button>
      </div>
    </Modal>
  )
}

function NoteDialog({ caseId, meeting, onClose, onDone }) {
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  return (
    <Modal
      open
      onClose={onClose}
      title="Write a new version of the notes"
      subtitle="Added as the next version. Earlier versions are never changed or removed — the amendment history is the point."
      width="max-w-2xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <Field label="Notes" required>
        <textarea
          className="field"
          rows={14}
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!body.trim()}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onDone(
                await write(`/${caseId}/meetings/${meeting.meeting_id}/notes`, {
                  body: { body },
                }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Save this version
        </Button>
      </div>
    </Modal>
  )
}

function SendDialog({ caseId, meeting, version, onClose, onDone }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Send version ${version} for confirmation`}
      subtitle="It goes to the person who was interviewed, and to nobody else — a confirmation is their statement about their own interview."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <p className="text-[12.5px] leading-relaxed text-muted">
        Only the latest version can be sent. Asking somebody to confirm
        superseded notes is a procedural error rather than a choice: whatever
        they say about that text, the investigation is working from different
        text.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              onDone(
                await write(`/${caseId}/meetings/${meeting.meeting_id}/notes/send`, {
                  body: { version: String(version) },
                }),
              )
              onClose()
            } catch (e) {
              setError(e)
            } finally {
              setBusy(false)
            }
          }}
        >
          Send for confirmation
        </Button>
      </div>
    </Modal>
  )
}
